import type { IconType } from "react-icons";
import Avatar, { type VariantColor } from "./UI/Avatar";
import Image from "./UI/Image";
import DateIndicator, {
  type DateType,
  type DisplayType,
} from "./DateIndicator";
import { Tooltip } from "./UI/Tooltip";
import type { ReactNode } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type IdentityBadgeProps = {
  avatarColor?: VariantColor;
  avatarSize?: "sm" | "md" | "lg" | "xl" | "2xl";
  mediaType: "picture" | "icon";
  media?: string | IconType;
  initials?: string;
  iconSize?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  title: string;
  date?: string | Date;
  dateType?: DateType;
  displayType?: DisplayType;
  description?: string;
  Btn?: ReactNode;
  is_doc?: boolean;
  active?: boolean;
  navigateTo?: string;
};

const textSizeMap: Record<
  NonNullable<IdentityBadgeProps["avatarSize"]>,
  string
> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xl: "text-lg",
  "2xl": "text-xl",
};

const iconSizeMap: Record<
  NonNullable<IdentityBadgeProps["iconSize"]>,
  string
> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
  "5xl": "text-5xl",
};

const IdentityBadge = ({
  avatarColor = "gray",
  avatarSize = "md",
  mediaType,
  media,
  initials,
  iconSize = "lg",
  title,
  date,
  dateType,
  displayType,
  description,
  Btn,
  is_doc = false,
  active = true,
  navigateTo,
}: IdentityBadgeProps) => {
  const AvatarIcon = mediaType === "icon" ? (media as IconType) : null;
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  const displayMedia = (type: "picture" | "icon") => {
    switch (type) {
      case "picture":
        return media && !imageError ? (
          <Image
            src={media as string}
            alt="Avatar"
            className={`${
              is_doc ? "size-8" : "w-full h-full object-cover rounded-full"
            } `}
            onError={() => setImageError(true)}
          />
        ) : (
          <h5 className={`${textSizeMap[avatarSize]} text-neutral-500`}>
            {initials}
          </h5>
        );
      case "icon":
        return AvatarIcon ? (
          <AvatarIcon className={`${iconSizeMap[iconSize]} text-neutral-400`} />
        ) : (
          <h5 className={textSizeMap[avatarSize]}>{initials}</h5>
        );
    }
  };

  return (
    <div
      className="flex items-center gap-2"
      onClick={navigateTo ? () => navigate(navigateTo) : undefined}
    >
      <Avatar variantColor={avatarColor} size={avatarSize}>
        {displayMedia(mediaType)}
      </Avatar>
      <Tooltip content={description ? `${title}\n${description}` : title}>
        <div className="flex flex-col gap-0.5 min-w-0">
          <h5
            className={`${active ? "text-neutral-950" : "text-neutral-400"}  font-medium truncate`}
          >
            {title}
          </h5>
          {description && (
            <h6 className="text-neutral-400 truncate">{description}</h6>
          )}
          {date && (
            <DateIndicator
              date={date}
              dateType={dateType}
              displayType={displayType}
            />
          )}
          {Btn && Btn}
        </div>
      </Tooltip>
    </div>
  );
};

export default IdentityBadge;
